import { useContext, useEffect } from 'react';
import { Theme } from '@sumtotal/react-foundation';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import getLogger from '../../../utils/logger';
import { postMessage, setCoreframeTitle, toggleVisibility } from '../services/coreIFrameHelper';
import CurrentThemeContext from '../../../contexts/ThemeContext';
import { evaluateLessVariables } from '../../../theme/services/themeHelper';
import { setCookieValue, removeCookie } from '../../../utils/cookieHelper';
import { ApplicationPaths, CookieNames } from '../../../authentication/AuthorizationConstants';
import useUserContext from '../../../common/hooks/useUserContext';
import useMessageContext from '../../../common/hooks/useMessageContext';
import useHeaderContext from '../../../common/hooks/useHeaderContext';
import authService from '../../../authentication/AuthorizeService';
import {
  removeLocalStorageItem,
  StorageConstants,
  setLocalStorageItem
} from '../../../utils/clientStorage';

function CoreMessageListener() {
  const { themeData, logoData, favIconData } = useContext(CurrentThemeContext);
  const [currentTheme, setCurrentTheme] = themeData;
  const setPreviewLogoUrl = logoData[1];
  const setFavIconUrl = favIconData[1];
  const { dispatch } = useUserContext();
  const headerData = useHeaderContext();
  const { setHeaderContext } = headerData;
  const messageContextData = useMessageContext();
  const { setMessageContext } = messageContextData;

  const history = useHistory();
  // On domain change clear rcore cookies, reload rcore & navigate to admin route.
  function onDomainChange(route) {
    let nextRoute = route;
    if (!nextRoute) {
      nextRoute = 'admindashboard';
    }
    // Clearing rcore cookies
    setCookieValue('sitemapHash', '');
    setCookieValue('dashboardHash', '');
    setCookieValue('rcoreDashboardHash', '');
    removeLocalStorageItem(StorageConstants.Keys.sitemap);

    // reload rcore
    window.location.assign(nextRoute);
  }

  function addRemoveBackdrop(action) {
    const backdropElements = document.getElementsByClassName('modal-backdrop');
    for (let index = 0; index < backdropElements.length; index++) {
      if (action === 'ADD') backdropElements[index].classList.add('modal-add-backdrop');
      else backdropElements[index].classList.remove('modal-add-backdrop');
    }
  }

  const validateMessageString = (message) => {
    const browserPluginMessagesToSkip = ['mgdata', 'nuanria_messaging'];
    if (typeof message !== 'string') {
      return true;
    }
    let isIdentified = false;
    browserPluginMessagesToSkip.forEach((skipItem) => {
      if (isIdentified) {
        return;
      }
      if (message.indexOf(skipItem) > -1) {
        isIdentified = true;
      }
    });
    return !isIdentified;
  };

  const MessageHandler = (e) => {
    if (e && e.data) {
      // check to make sure that we're not goiong to get some XSS by insuring that the script
      // is invoked by our origin.
      if (window.staticData && !e.origin.match(window.staticData.safeOrigin)) {
        getLogger().warn('Received a post from a window which did not have the same origin.');
        return;
      }

      let messageData = e.data;
      if (typeof messageData === 'string') {
        messageData = parseJson(messageData);
      }
      const { action, parameters } = messageData;
      switch (action) {
        // Update page title
        case 'RCORE_UPDATE_TITLE': {
          document.title = parameters.title;
          // Update core iframe title
          setCoreframeTitle(parameters.frameTitle);
          break;
        }
        case 'CORE_FRAME_LOADED': {
          setHeaderContext((state) => ({
            ...state,
            coreFrameLoaded: true
          }));
          break;
        }
        case 'CORE_SLIDEOUT_OPENED': {
          const isRcoreRoute = window.location.pathname.toLowerCase().indexOf('/c/') < 0;
          toggleVisibility(true, isRcoreRoute);
          if (window.location.pathname.indexOf('/dashboardManager/designer/') > -1) {
            setHeaderContext((state) => ({
              ...state,
              closeRCoreSlideout: true
            }));
          }
          break;
        }
        case 'CORE_SLIDEOUT_CLOSED': {
          if (window.location.pathname.toLowerCase().indexOf('/c/') < 0) {
            toggleVisibility(false);
          }
          if (e.data.parameters.elementId && e.data.parameters.elementId !== '') {
            document.getElementById(e.data.parameters.elementId).focus();
          }
          break;
        }
        // Update theme
        case 'RCORE_UPDATE_THEME': {
          if (currentTheme && currentTheme.variables) {
            evaluateLessVariables(parameters.themeVarsObject)
              .then((varsJson) => {
                const previewTheme = Theme(varsJson);
                setCurrentTheme(previewTheme);
                setPreviewLogoUrl(parameters.clientLogo);
                setFavIconUrl(parameters.clientFavIcon);
                return null;
              })
              .catch((errorResponse) => {
                getLogger().error(errorResponse);
              });
          }
          break;
        }
        // Update the url to handle deep link navigation
        case 'RCORE_UPDATE_NAVIGATION_URL': {
          const parsedUrl = queryString.parseUrl(parameters.url);
          if (parsedUrl.url === '/search') {
            return;
          }
          if (!parsedUrl.query.isDeepLink) {
            parsedUrl.query.isDeepLink = 1;
          }
          if (parsedUrl.query.rc) {
            delete parsedUrl.query.rc;
          }
          const prefix = parsedUrl.url.indexOf('/') === 0 ? '' : '/';
          const url = `/rcore/c${prefix}${queryString.stringifyUrl(parsedUrl)}`;
          // If next route is a core route and current route is rcore route, make sure that iframe is visible.
          if (
            url.toLowerCase().indexOf('/c/') > 0 &&
            window.location.pathname.toLowerCase().indexOf('/c/') < 0
          ) {
            setHeaderContext((state) => ({
              ...state,
              considerRcorePage: false
            }));
            toggleVisibility(true, true);
          }
          window.history.replaceState('', '', url);
          break;
        }
        // Navigate to RCore node URL without reloading
        case 'NAVIGATE_TO_RCORE': {
          history.push(parameters.url);
          break;
        }

        case 'RCORE_SECURITY_QUESTIONS_ANSWERED': {
          // Setting UserJustAnsweredSecurityQuestion value  false for enable rcore components.
          const mustAnswerSecurityQuestions = parameters.mustSetSecurityQuestions;
          dispatch({ type: 'USER_SECURITY_QUESTIONS', mustAnswerSecurityQuestions });
          setLocalStorageItem('MustAnswerSecurityQuestions', mustAnswerSecurityQuestions);
          break;
        }
        // Event triggered on domain change.
        case 'RCORE_SWITCH_USER_DOMAIN': {
          setLocalStorageItem('MustAnswerSecurityQuestions', false);
          onDomainChange(parameters.route);
          break;
        }

        case 'RCORE_EMULATE_EMPLOYEE': {
          authService
            .removeAccessToken()
            .then(() => {
              window.location.assign(parameters.route);
              return null;
            })
            .catch((err) => {
              getLogger().error(err);
            });
          break;
        }
        case 'RCORE_SESSION_EXPIRED': {
          removeCookie(CookieNames.ApiSecurity);
          authService
            .removeAccessToken()
            .then(() => {
              window.location.assign(e.data.parameters.route);
              return null;
            })
            .catch((err) => {
              getLogger().error(err);
            });
          break;
        }
        case 'RCORE_SESSION_LOGOFF': {
          history.push(ApplicationPaths.Logout);
          break;
        }
        case 'RCORE_SEARCH_CONTEXT_UPDATE': {
          setHeaderContext((state) => ({
            ...state,
            searchText: parameters.text,
            searchContext: parameters.context,
            view: parameters.view
          }));
          break;
        }
        // Update user data
        case 'RCORE_UPDATE_USER_DATA': {
          const userData = {};
          if (parameters.imagePath !== undefined) {
            userData.image = parameters.imagePath;
          } else {
            if (parameters.fullName !== undefined) {
              userData.name = parameters.fullName;
            }
            if (parameters.jobTitle !== undefined) {
              userData.jobTitle = parameters.jobTitle;
            }
          }
          dispatch({ type: 'UPDATE_USER_DETAILS', userData });
          break;
        }
        case 'RCORE_GOAL_DETAIL_SLIDEOUT_CLOSE': {
          if (parameters.message !== undefined && parameters.message !== '') {
            setMessageContext((state) => ({
              ...state,
              text: parameters.message,
              refresh: true,
              severity: parameters.severity
            }));
          }
          break;
        }
        // current logic is very similar to RCORE_GOAL_DETAIL_SLIDEOUT_CLOSE, will add more logic later
        case 'RCORE_SKILL_SLIDEOUT_CLOSE': {
          const isMarketComparisonRoute =
            window.location.pathname.indexOf('/marketComparison/') >= 0;
          if (
            parameters.message !== undefined &&
            parameters.message !== '' &&
            isMarketComparisonRoute
          ) {
            setMessageContext((state) => ({
              ...state,
              text: parameters.message,
              refresh: true,
              severity: parameters.severity,
              data: parameters.internalSkillData
            }));
          }

          break;
        }
        // Refresh timeline count
        case 'RCORE_REFRESH_TIMELINE_COUNT': {
          setHeaderContext((state) => ({
            ...state,
            refreshTimeline: true
          }));
          break;
        }
        // Reset Search Term and Search values
        case 'RCORE_RESET_SEARCH_VALUES': {
          setHeaderContext((state) => ({
            ...state,
            resetSearchValues: true
          }));
          break;
        }
        case 'RCORE_UPDATE_CART': {
          setHeaderContext((state) => ({
            ...state,
            cartItemsCount: parameters.cartItemsCount
          }));
          break;
        }
        case 'CORE_MODAL_OPENED': {
          addRemoveBackdrop('ADD');
          break;
        }
        case 'CORE_MODAL_CLOSED': {
          addRemoveBackdrop('REMOVE');
          break;
        }
        /* Default case - redirect message to core as pillars post messages to top object
         * This listener wouldn't directly handle pillar messages as of now
         */
        default: {
          // Excluding the my guide messages.
          if (e.data && validateMessageString(e.data)) {
            postMessage(null, e.data, true);
          }
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', MessageHandler);
    return () => window.removeEventListener('message', MessageHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

/**
 * Parse the string into json (if valid). returns same string if failed to convert
 * @param {String} jsonString
 * @returns {String | Object}
 */
const parseJson = (jsonString) => {
  try {
    return JSON.parse(jsonString);
  } catch {
    return jsonString;
  }
};

export default CoreMessageListener;
